import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

/**
 * Rate-vs-time step-line chart for one variable-rate bond.
 *   - stepAfter Line over the full merged timeline (feed history + scheduled +
 *     manual). A reset holds flat until the next point; linear interpolation
 *     would misrepresent it (spec 7).
 *   - Two overlaid Scatter series so feed (blue) and manual (amber) points are
 *     visually distinct and separately clickable.
 * Clicking a MANUAL point calls onPointClick(point) (edit/delete). Clicking a
 * FEED point calls onFeedPointClick(point) ("override from this date"). The
 * parent owns the editor UI + mutations.
 */
const FEED_COLOR = '#3b82f6'; // blue-500
const MANUAL_COLOR = '#f59e0b'; // amber-500

const VariableRateChart = ({
  timeline = [],
  onPointClick = () => {},
  onFeedPointClick = () => {},
  darkMode = false,
}) => {
  if (!timeline || timeline.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No rate history for this bond yet.
      </div>
    );
  }

  // recharts wants numeric x for a proper step; keep the ISO date as the axis
  // label and use the index for spacing so unevenly-spaced resets still step.
  const data = timeline.map((p, i) => ({ ...p, idx: i }));
  const feedPoints = data.filter((p) => p.source !== 'manual');
  const manualPoints = data.filter((p) => p.source === 'manual');

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div
        className={`p-2 rounded shadow border text-sm ${
          darkMode
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <div className="font-medium">{p.effective_date}</div>
        <div>{Number(p.rate).toFixed(3)}%</div>
        <div className="text-xs opacity-70">{p.source}</div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
        <XAxis
          dataKey="effective_date"
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          style={{ fontSize: '12px' }}
        />
        <YAxis
          tickFormatter={(v) => `${v}%`}
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          style={{ fontSize: '12px' }}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="stepAfter"
          dataKey="rate"
          data={data}
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          strokeWidth={2}
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        />
        <Scatter
          name="feed"
          dataKey="rate"
          data={feedPoints}
          fill={FEED_COLOR}
          onClick={(pt) => onFeedPointClick(pt && pt.payload ? pt.payload : pt)}
        />
        <Scatter
          name="manual"
          dataKey="rate"
          data={manualPoints}
          fill={MANUAL_COLOR}
          onClick={(pt) => onPointClick(pt && pt.payload ? pt.payload : pt)}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default VariableRateChart;
